import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { vetrineContentService } from '../../../services/vetrineContentService';

const ServicesManager = ({ storeId }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    categoria: '',
    prezzo: '',
    durata: '',
    disponibilita: '',
    caratteristiche: [],
    icona: 'Briefcase',
    inEvidenza: false,
    ordine: 0
  });
  const [caratteristicaInput, setCaratteristicaInput] = useState('');

  useEffect(() => {
    loadServices();
  }, [storeId]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await vetrineContentService?.getServicesByStore(storeId);
      setServices(data);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      const serviceData = {
        ...formData,
        storeId,
        prezzo: parseFloat(formData?.prezzo) || 0
      };

      if (editingService) {
        await vetrineContentService?.updateService(editingService?.id, serviceData);
      } else {
        await vetrineContentService?.createService(serviceData);
      }

      await loadServices();
      resetForm();
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Errore nel salvare il servizio: ' + error?.message);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      nome: service?.nome || '',
      descrizione: service?.descrizione || '',
      categoria: service?.categoria || '',
      prezzo: service?.prezzo || '',
      durata: service?.durata || '',
      disponibilita: service?.disponibilita || '',
      caratteristiche: service?.caratteristiche || [],
      icona: service?.icona || 'Briefcase',
      inEvidenza: service?.inEvidenza ?? false,
      ordine: service?.ordine || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (serviceId) => {
    if (!confirm('Sei sicuro di voler eliminare questo servizio?')) return;
    
    try {
      await vetrineContentService?.deleteService(serviceId);
      await loadServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Errore nell\'eliminare il servizio: ' + error?.message);
    }
  };

  const addCaratteristica = () => {
    if (caratteristicaInput?.trim()) {
      setFormData({
        ...formData,
        caratteristiche: [...formData?.caratteristiche, caratteristicaInput?.trim()]
      });
      setCaratteristicaInput('');
    }
  };

  const removeCaratteristica = (index) => {
    setFormData({
      ...formData,
      caratteristiche: formData?.caratteristiche?.filter((_, i) => i !== index)
    });
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descrizione: '',
      categoria: '',
      prezzo: '',
      durata: '',
      disponibilita: '',
      caratteristiche: [],
      icona: 'Briefcase',
      inEvidenza: false,
      ordine: 0
    });
    setCaratteristicaInput('');
    setEditingService(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="text-center py-8">Caricamento servizi...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Servizi ({services?.length})</h2>
        <Button onClick={() => setShowForm(!showForm)}>
          <Icon name={showForm ? 'X' : 'Plus'} size={20} />
          {showForm ? 'Annulla' : 'Aggiungi Servizio'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-muted/30 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingService ? 'Modifica Servizio' : 'Nuovo Servizio'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome Servizio *"
              value={formData?.nome}
              onChange={(e) => setFormData({ ...formData, nome: e?.target?.value })}
              required
            />
            <Input
              label="Categoria"
              value={formData?.categoria}
              onChange={(e) => setFormData({ ...formData, categoria: e?.target?.value })}
            />
            <Input
              label="Prezzo (€)"
              type="number"
              step="0.01"
              value={formData?.prezzo}
              onChange={(e) => setFormData({ ...formData, prezzo: e?.target?.value })}
            />
            <Input
              label="Durata"
              placeholder="es. 30 minuti"
              value={formData?.durata}
              onChange={(e) => setFormData({ ...formData, durata: e?.target?.value })}
            />
            <Input
              label="Disponibilità"
              placeholder="es. Lun-Ven 9:00-18:00"
              value={formData?.disponibilita}
              onChange={(e) => setFormData({ ...formData, disponibilita: e?.target?.value })}
              className="md:col-span-2"
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Descrizione</label>
              <textarea
                value={formData?.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e?.target?.value })}
                rows={3}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Caratteristiche</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={caratteristicaInput}
                  onChange={(e) => setCaratteristicaInput(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), addCaratteristica())}
                  placeholder="Aggiungi caratteristica"
                  className="flex-1 px-4 py-2 rounded-lg border border-input bg-background"
                />
                <Button type="button" onClick={addCaratteristica}>
                  <Icon name="Plus" size={20} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData?.caratteristiche?.map((car, index) => (
                  <span key={index} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm flex items-center gap-2">
                    {car}
                    <button type="button" onClick={() => removeCaratteristica(index)}>
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData?.inEvidenza}
                  onChange={(e) => setFormData({ ...formData, inEvidenza: e?.target?.checked })}
                  className="rounded"
                />
                <span className="text-sm">In Evidenza</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button type="submit">
              <Icon name="Save" size={20} />
              {editingService ? 'Aggiorna' : 'Salva'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>
              Annulla
            </Button>
          </div>
        </form>
      )}

      {services?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="Briefcase" size={48} className="mx-auto mb-4 opacity-50" />
          <p>Nessun servizio presente</p>
        </div>
      ) : (
        <div className="space-y-4">
          {services?.map((service) => (
            <div key={service?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name={service?.icona || 'Briefcase'} size={24} className="text-primary" />
                    <div>
                      <h3 className="font-semibold text-lg">{service?.nome}</h3>
                      {service?.categoria && (
                        <span className="text-xs text-muted-foreground">{service?.categoria}</span>
                      )}
                    </div>
                  </div>
                  {service?.descrizione && (
                    <p className="text-sm text-muted-foreground mb-3">{service?.descrizione}</p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm">
                    {service?.prezzo > 0 && (
                      <span className="font-semibold">€{service?.prezzo}</span>
                    )}
                    {service?.durata && (
                      <span className="text-muted-foreground">⏱ {service?.durata}</span>
                    )}
                    {service?.disponibilita && (
                      <span className="text-muted-foreground">📅 {service?.disponibilita}</span>
                    )}
                  </div>
                  {service?.caratteristiche?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {service?.caratteristiche?.map((car, index) => (
                        <span key={index} className="px-2 py-1 bg-muted text-xs rounded-full">
                          {car}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(service)}
                    className="p-2 hover:bg-muted rounded-lg transition-smooth"
                  >
                    <Icon name="Edit" size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(service?.id)}
                    className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-smooth"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServicesManager;